/**
 * Office-style Desktop — board "6 · Office-style Desktop".
 *
 * The menu as a RIBBON: top-level folders are ribbon tabs (a "Home" tab holds
 * the top-level screens, "View" the window commands), the selected tab's
 * sub-folders are groups of large buttons, and anything deeper is a drop-down
 * cascade (see office/Ribbon). Around it, the Office idiom:
 *   - the Quick Access Toolbar = Pinned screens, published into the HOST's
 *     top bar (which draws the title, Apps, Search, Work List, notifications
 *     and the user menu above this layout — none of that is redrawn here);
 *   - the "Menu" panel (File / backstage): Home, the whole menu, Pinned,
 *     Recent, Open editors;
 *   - a path bar with the screen's title, ⓘ and the pin star;
 *   - SHEET TABS along the bottom for open screens and open editors, a View →
 *     Switch Windows drop-down for both, and a status bar.
 *
 * Presentational only: the screen, Pinned, Recent, open screens and open
 * editors all live in `model`. View state kept here: the selected ribbon tab
 * (it follows the current screen's top folder until the user picks one),
 * collapsed ribbon / status bar (remembered in localStorage) and which panel
 * or pop-up is open.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { tw } from '../../testHooks'
import { EmptyPane } from '../nav/parts/EmptyPane'
import { ShellFrame } from '../nav/ShellFrame'
import { S } from '../nav/tokens'
import type { RunNavItem } from '../nav/types'
import { PathBar, StatusBar } from './office/Bars'
import { MenuPanel, type PanelSection } from './office/MenuPanel'
import { QuickAccess } from './office/QuickAccess'
import { HOME_TAB, Ribbon, VIEW_TAB, type RibbonTab } from './office/Ribbon'
import { ACCENT_SOLID, OFFICE_CSS, usePersistedFlag } from './office/shared'
import { SheetTabs } from './office/SheetTabs'
import type { ShellLayoutProps } from './types'

const COLLAPSED_KEY = 'twasta:shell:office:ribbonCollapsed'
const STATUS_KEY = 'twasta:shell:office:statusBar'

export default function OfficeLayout({
  model, appTitle, themeColor, compact = false, renderObject, headerExtras,
  emptyNavMessage, emptyPaneMessage, setTopBarSlots,
}: ShellLayoutProps) {
  const full = model.fullScreen
  const current = model.current
  const mainId = `${useId()}-main`
  const [collapsed, setCollapsed] = usePersistedFlag(COLLAPSED_KEY, false)
  const [statusOn, setStatusOn] = usePersistedFlag(STATUS_KEY, true)

  // ── Menu panel ──────────────────────────────────────────────────────
  const [panel, setPanel] = useState<PanelSection | null>(null)
  const panelReturn = useRef<HTMLElement | null>(null)
  const menuBtnRef = useRef<HTMLButtonElement>(null)
  const openPanel = useCallback((section: PanelSection, from?: HTMLElement | null) => {
    panelReturn.current = from ?? (document.activeElement as HTMLElement | null)
    setPanel(section)
  }, [])
  const closePanel = useCallback((restore: boolean) => {
    setPanel(null)
    if (!restore) return
    const back = panelReturn.current && panelReturn.current.isConnected ? panelReturn.current : menuBtnRef.current
    back?.focus()
  }, [])
  // "Open editors" from the cap notice: the panel's editors section.
  useEffect(() => {
    if (!model.editorsListRequest) return
    openPanel('editors')
  }, [model.editorsListRequest]) // eslint-disable-line react-hooks/exhaustive-deps
  // Full screen hides every piece of chrome, the panel included.
  useEffect(() => { if (full) setPanel(null) }, [full])

  // ── Quick Access Toolbar, in the host's top bar ─────────────────────
  const { pinned, open, movePin } = model
  const currentId = current?.id ?? null
  const panelIsPinned = panel === 'pinned'
  // Opening from the bar leaves the Menu panel (it covers the screen).
  const openFromBar = useCallback((item: RunNavItem) => { setPanel(null); open(item) }, [open])
  const slots = useMemo(() => ({
    left: (
      <QuickAccess pinned={pinned} currentId={currentId} onOpen={openFromBar}
        onCustomize={(el) => openPanel('pinned', el)} customizeOpen={panelIsPinned} onMove={movePin} />
    ),
  }), [pinned, currentId, openFromBar, openPanel, panelIsPinned, movePin])
  // Published after EVERY render, not only when `slots` changes: the shell
  // clears the slots in its own effect when the layout changes, which runs
  // AFTER this child's effect on mount — a deps-guarded publish would leave
  // the bar empty. Re-publishing the same object is a no-op (state bail-out).
  useEffect(() => { setTopBarSlots(slots) })
  useEffect(() => () => setTopBarSlots(null), [setTopBarSlots])

  // ── Ribbon tabs ─────────────────────────────────────────────────────
  const tabs = useMemo<RibbonTab[]>(() => {
    const folders = model.roots.filter((n) => n.isFolder)
    const hasHome = model.roots.some((n) => !n.isFolder) || model.roots.length === 0
    return [
      ...(hasHome ? [{ id: HOME_TAB, label: 'Home' }] : []),
      ...folders.map((f) => ({ id: f.item.id, label: f.item.title, node: f })),
      { id: VIEW_TAB, label: 'View' },
    ]
  }, [model.roots])
  /** The tab a screen lives under: its top folder, else Home. */
  const tabFor = (item: RunNavItem | null): string => {
    const top = item ? model.pathOf(item)[0] : undefined
    if (top && tabs.some((t) => t.id === top.id)) return top.id
    if (item && tabs.some((t) => t.id === HOME_TAB)) return HOME_TAB
    return tabs[0]?.id ?? VIEW_TAB
  }
  // null = follow the current screen; the user's own pick holds until the
  // current screen changes.
  const [picked, setPicked] = useState<string | null>(null)
  useEffect(() => { setPicked(null) }, [currentId])
  const activeTab = picked && tabs.some((t) => t.id === picked) ? picked : tabFor(current)

  // A folder crumb: the Menu panel's tree, opened at that folder.
  const onFolder = useCallback((folder: RunNavItem) => {
    model.setExpanded([...model.pathOf(folder).map((p) => p.id), folder.id], true)
    openPanel('open')
  }, [model, openPanel])

  return (
    <div {...tw('action', 'shell-office')} style={{
      position: 'relative', display: 'flex', flexDirection: 'column', flex: 1, width: '100%', height: '100%',
      minHeight: 0, overflow: 'hidden', background: S.bg,
    }}>
      <style>{OFFICE_CSS}</style>
      {!full && (
        <Ribbon
          model={model}
          tabs={tabs}
          activeTab={activeTab}
          onSelectTab={setPicked}
          collapsed={collapsed}
          onCollapsed={setCollapsed}
          statusOn={statusOn}
          onStatus={setStatusOn}
          menuOpen={!!panel}
          onOpenMenu={(el) => (panel ? closePanel(false) : openPanel('open', el))}
          menuButtonRef={menuBtnRef}
          emptyNavMessage={emptyNavMessage}
          compact={compact}
        />
      )}
      {!full && current && (
        <PathBar model={model} item={current} extras={headerExtras?.(current)} onFolder={onFolder} compact={compact} />
      )}
      {/* The screen — the same element in and out of full screen, so the
          screen is never remounted by the toggle. */}
      <main id={mainId} style={{
        flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: S.bg,
      }}>
        <ShellFrame
          model={model}
          renderObject={renderObject}
          padding={compact ? '0.5rem' : '0.5rem 1rem 1rem'}
          empty={(
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <EmptyPane model={model} appTitle={appTitle} message={emptyPaneMessage} accent={themeColor} />
              {model.roots.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '0 16px 16vh' }}>
                  <button type="button" className="tw-oc-solid" onClick={(e) => openPanel('open', e.currentTarget)}
                    aria-haspopup="dialog" {...tw('action', 'shell-office-open-screen')}
                    style={{
                      height: 40, padding: '0 18px', border: 'none', borderRadius: 6, background: ACCENT_SOLID,
                      color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
                    }}>
                    Open a screen
                  </button>
                </div>
              )}
            </div>
          )}
        />
      </main>
      {!full && <SheetTabs model={model} controls={mainId} onNew={(el) => openPanel('open', el)} />}
      {!full && statusOn && <StatusBar model={model} onOpenEditors={(el) => openPanel('editors', el)} />}
      {!full && panel && (
        <MenuPanel model={model} section={panel} onSection={setPanel} onClose={closePanel}
          emptyNavMessage={emptyNavMessage} />
      )}
    </div>
  )
}
